import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AddSubWorksWbsNode,
  AddSubWorksWorkGroupNode,
  RateGroupAPI,
  TenderTreeAPI,
} from "../Services/Api";
import { useSession } from "../Contexts/SessionContext";

type StageItemFormProps = {
  tenderNo: number;
  contractNode?: number;
  onItemsAdded?: () => void;
  onCancel?: () => void;
};

const StageItemForm: React.FC<StageItemFormProps> = ({
  tenderNo,
  contractNode = 0,
  onItemsAdded,
  onCancel,
}) => {
  const { sessionNo } = useSession();

  const [tree, setTree] = useState<AddSubWorksWbsNode[]>([]);
  const [expandedWbs, setExpandedWbs] = useState<Set<number>>(new Set());
  const [selectedWorkGroups, setSelectedWorkGroups] = useState<Set<number>>(
    new Set()
  );
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadTree = useCallback(async () => {
    if (!Number.isInteger(tenderNo) || tenderNo <= 0) {
      setError("A valid Tender No. is required.");
      setTree([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // This endpoint must return WBS nodes, each node's children, and its WorkGroups.
      const response = await TenderTreeAPI.getAddSubWorksTree(tenderNo);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setTree(rows);

      if (!Array.isArray(response?.data)) {
        setError("The server did not return a valid WBS and WorkGroups tree.");
      }
    } catch (err: any) {
      console.error("Unable to load WBS and WorkGroups:", err);
      setTree([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load the WBS and WorkGroups tree."
      );
    } finally {
      setLoading(false);
    }
  }, [tenderNo]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const flattenWorkGroups = useCallback(
    (nodes: AddSubWorksWbsNode[]): AddSubWorksWorkGroupNode[] => {
      const result: AddSubWorksWorkGroupNode[] = [];

      const visit = (items: AddSubWorksWbsNode[]) => {
        items.forEach((node) => {
          (node.workGroups || []).forEach((workGroup) => result.push(workGroup));
          visit(node.children || []);
        });
      };

      visit(nodes);
      return result;
    },
    []
  );

  const allWorkGroups = useMemo(
    () => flattenWorkGroups(tree),
    [tree, flattenWorkGroups]
  );

  const visibleWorkGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allWorkGroups;

    return allWorkGroups.filter((workGroup) =>
      `${workGroup.workGroup || ""} ${workGroup.workGroupDesc || ""} ${workGroup.workGroupNo}`
        .toLowerCase()
        .includes(term)
    );
  }, [allWorkGroups, search]);

  const visibleWorkGroupIds = useMemo(
    () => new Set(visibleWorkGroups.map((workGroup) => workGroup.workGroupNo)),
    [visibleWorkGroups]
  );

  const toggleWbs = (treeNodeNo: number) => {
    setExpandedWbs((previous) => {
      const next = new Set(previous);
      if (next.has(treeNodeNo)) next.delete(treeNodeNo);
      else next.add(treeNodeNo);
      return next;
    });
  };

  const toggleWorkGroup = (workGroupNo: number) => {
    setSelectedWorkGroups((previous) => {
      const next = new Set(previous);
      if (next.has(workGroupNo)) next.delete(workGroupNo);
      else next.add(workGroupNo);
      return next;
    });
    setError("");
  };

  const toggleAllVisible = () => {
    setSelectedWorkGroups((previous) => {
      const next = new Set(previous);
      const allVisibleSelected =
        visibleWorkGroups.length > 0 &&
        visibleWorkGroups.every((workGroup) => next.has(workGroup.workGroupNo));

      visibleWorkGroups.forEach((workGroup) => {
        if (allVisibleSelected) next.delete(workGroup.workGroupNo);
        else next.add(workGroup.workGroupNo);
      });

      return next;
    });
  };

  const containsVisibleWorkGroup = (
    node: AddSubWorksWbsNode,
    visibleIds: Set<number>
  ): boolean => {
    if ((node.workGroups || []).some((workGroup) => visibleIds.has(workGroup.workGroupNo))) {
      return true;
    }
    return (node.children || []).some((child) =>
      containsVisibleWorkGroup(child, visibleIds)
    );
  };

  const handleAddItem = async () => {
    setError("");

    if (!Number.isInteger(tenderNo) || tenderNo <= 0) {
      setError("A valid Tender No. is required.");
      return;
    }

   /*  if (!Number.isInteger(contractNode) || contractNode <= 0) {
      setError("Please select a valid contract section.");
      return;
    } */

    if (!sessionNo || Number(sessionNo) <= 0) {
      setError("Session not found. Please select the project again.");
      return;
    }

    if (selectedWorkGroups.size === 0) {
      setError("Please select at least one WorkGroup.");
      return;
    }

    const selected = allWorkGroups.filter((workGroup) =>
      selectedWorkGroups.has(workGroup.workGroupNo)
    );

    if (selected.length === 0) {
      setError("The selected WorkGroups are no longer available. Refresh and try again.");
      return;
    }

    setSaving(true);
    try {
      // The existing API accepts one WorkGroup per request.
      for (const workGroup of selected) {
        await RateGroupAPI.addStageItem({
          tenderNo,
          workGroup: workGroup.workGroupNo,
          //contractNode,
            contractNode: contractNode ?? 0,
          sessionNo: Number(sessionNo),
        });
      }

      setSelectedWorkGroups(new Set());
      onItemsAdded?.();
    } catch (err: any) {
      console.error("Unable to add Stage Items:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to add all selected Stage Items. Some items may already have been saved; check the contract list before retrying."
      );
    } finally {
      setSaving(false);
    }
  };

  const renderTree = (
    nodes: AddSubWorksWbsNode[],
    depth = 0
  ): React.ReactNode =>
    nodes.map((node) => {
      const isExpanded = expandedWbs.has(node.treeNodeNo);
      const workGroups = (node.workGroups || []).filter((workGroup) =>
        visibleWorkGroupIds.has(workGroup.workGroupNo)
      );
      const children = node.children || [];
      const hasChildren = children.length > 0;
      const hasWorkGroups = workGroups.length > 0;
      const hasVisibleDescendant = children.some((child) =>
        containsVisibleWorkGroup(child, visibleWorkGroupIds)
      );

      if (search.trim() && !hasWorkGroups && !hasVisibleDescendant) return null;

      return (
        <div key={node.treeNodeNo}>
          <div
            className="flex min-h-9 items-center gap-2 border-b border-gray-100 px-2 py-1 hover:bg-gray-50"
            style={{ paddingLeft: 8 + depth * 20 }}
          >
            <button
              type="button"
              aria-label={isExpanded ? "Collapse WBS" : "Expand WBS"}
              onClick={() => toggleWbs(node.treeNodeNo)}
              disabled={!hasChildren && !hasWorkGroups}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-black hover:bg-gray-200 disabled:opacity-40"
            >
              <span className="text-[13px]">
                {isExpanded ? "▼" : "▶"}
              </span>
            </button>

            <span className="text-[13px] font-bold text-black">
              {node.workTreeName || `WBS ${node.treeNodeNo}`}
            </span>

            {hasWorkGroups && (
              <span className="ml-auto text-[13px] font-normal text-black">
                {workGroups.length} WorkGroup{workGroups.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {isExpanded && (
            <div>
              {workGroups.map((workGroup) => (
                <label
                  key={workGroup.workGroupNo}
                  className="flex min-h-9 cursor-pointer items-start gap-2 border-b border-gray-100 px-3 py-2 hover:bg-gray-50"
                  style={{ paddingLeft: 34 + depth * 20 }}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    checked={selectedWorkGroups.has(workGroup.workGroupNo)}
                    onChange={() => toggleWorkGroup(workGroup.workGroupNo)}
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-normal text-black">
                      {workGroup.workGroup || `WorkGroup ${workGroup.workGroupNo}`}
                    </span>
                    {workGroup.workGroupDesc && (
                      <span className="block text-[13px] font-normal text-black">
                        {workGroup.workGroupDesc}
                      </span>
                    )}
                    <span className="block text-[13px] font-normal text-black">
                      WorkGroup No: {workGroup.workGroupNo}
                    </span>
                  </span>
                </label>
              ))}

              {hasChildren && renderTree(children, depth + 1)}
            </div>
          )}
        </div>
      );
    });

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-white text-black"
      style={{ fontFamily: "Segoe UI, sans-serif", fontSize: 13, fontWeight: 400 }}
    >
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-[13px] font-normal text-black">Add Stage Items</div>
        <div className="mt-1 text-[13px] font-normal text-black">
          Select WorkGroups under their WBS headings.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search WorkGroups..."
          className="min-w-[180px] flex-1 rounded border border-gray-300 px-3 py-2 text-[13px] font-normal text-black outline-none focus:border-black"
          style={{ fontFamily: "Segoe UI, sans-serif" }}
        />
        <button
          type="button"
          onClick={toggleAllVisible}
          disabled={visibleWorkGroups.length === 0 || loading}
          className="rounded border border-gray-300 px-3 py-2 text-[13px] font-normal text-black hover:bg-gray-50 disabled:opacity-50"
        >
          Select / clear visible
        </button>
        <button
          type="button"
          onClick={() => void loadTree()}
          disabled={loading || saving}
          className="rounded border border-gray-300 px-3 py-2 text-[13px] font-normal text-black hover:bg-gray-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" className="mx-3 mt-3 rounded border border-red-200 px-3 py-2 text-[13px] font-normal text-black">
          {error}
        </div>
      )}

      <div className="min-h-[220px] flex-1 overflow-auto p-2">
        {loading ? (
          <div className="p-8 text-center text-[13px] text-black">
            Loading WBS and WorkGroups...
          </div>
        ) : tree.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-black">
            No WBS or WorkGroups found for this tender.
          </div>
        ) : (
          renderTree(tree)
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
        <span className="text-[13px] font-normal text-black">
          {selectedWorkGroups.size} WorkGroup{selectedWorkGroups.size === 1 ? "" : "s"} selected
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded border border-gray-300 px-4 py-2 text-[13px] font-normal text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleAddItem()}
            disabled={saving || loading || selectedWorkGroups.size === 0}
            className="rounded border border-gray-300 px-4 py-2 text-[13px] font-normal text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Adding..." : "Add Stage Items"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StageItemForm;
